/**
 * The find and replace bar (Requirements 7.9, 7.10, 3.1).
 *
 * `Ctrl+F` opens it in find mode and `Ctrl+H` in replace mode, which is the pair of
 * bindings Requirement 3.1 names. It is a strip rather than a modal dialog for one
 * reason: find *moves the selection*, so the grid has to stay visible and scrollable
 * while it is open.
 *
 * The bar owns no search logic. `Find next` asks the Query_Engine for the next match in
 * scan order (Requirement 7.9) and `Replace all` sends a `replaceAll` command
 * (Requirement 7.10); both answers, including "no match" and the replacement count,
 * come back from the worker and are reported here verbatim. That keeps one definition
 * of "contains" in the app.
 */

import { button, el } from './dom.js';

export type FindMode = 'find' | 'replace';

export interface FindBarOptions {
  /** Runs a find from the current selection. Reports what to show the user. */
  onFindNext: (term: string) => void;
  /** Runs a replace-all over the search scope. */
  onReplaceAll: (term: string, replacement: string) => void;
  /** Called when the bar closes, so focus can return to the grid. */
  onClose: () => void;
  /**
   * Offered every keydown the bar does not own, so the app's shortcuts keep working
   * while the search field has focus (Requirement 3.6).
   *
   * Opening the bar moves focus into it, and without this the grid's binding table would
   * stop seeing `Ctrl+`-combinations the moment a user pressed `Ctrl+F` — the browser
   * would take `Ctrl+S` and `Ctrl+H` back. `Ctrl+H` in particular has to reach the
   * handler: it is how a user switches this bar from find to replace.
   */
  onKey?: (event: KeyboardEvent) => void;
}

export interface FindBar {
  element: HTMLElement;
  /** Opens the bar, optionally revealing the replacement field. */
  open(mode?: FindMode): void;
  close(): void;
  isOpen(): boolean;
  /** The search term currently typed. */
  term(): string;
  /** The replacement currently typed. */
  replacement(): string;
  /** Shows a result line, such as "no match" or a replacement count. */
  setResult(text: string): void;
}

export function createFindBar(options: FindBarOptions): FindBar {
  const term = el('input', {
    className: 'find-term',
    testid: 'find-term',
    ariaLabel: 'Find what',
    attrs: { type: 'text', placeholder: 'Find what' },
  });
  const replacement = el('input', {
    className: 'find-replacement',
    testid: 'find-replacement',
    ariaLabel: 'Replace with',
    attrs: { type: 'text', placeholder: 'Replace with' },
  });
  const result = el('span', {
    className: 'find-result',
    testid: 'find-result',
    role: 'status',
    attrs: { 'aria-live': 'polite' },
  });

  const findNext = button('Find next', () => options.onFindNext(term.value), 'find-next');
  const replaceAll = button(
    'Replace all',
    () => options.onReplaceAll(term.value, replacement.value),
    'find-replace-all',
  );
  const close = button('Close', () => bar.close(), 'find-close');

  const element = el(
    'div',
    {
      className: 'find-bar',
      testid: 'find-bar',
      role: 'search',
      attrs: { hidden: 'hidden' },
    },
    [term, replacement, findNext, replaceAll, close, result],
  );

  // Enter repeats the find, which is what a user who just typed a term expects, and
  // Escape closes the bar exactly as it cancels an edit (Requirement 2.4's mental model).
  element.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      options.onFindNext(term.value);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      bar.close();
      return;
    }
    options.onKey?.(event);
  });

  const bar: FindBar = {
    element,

    open(mode: FindMode = 'find'): void {
      element.removeAttribute('hidden');
      element.dataset['mode'] = mode;
      if (mode === 'replace') replacement.removeAttribute('hidden');
      else replacement.setAttribute('hidden', 'hidden');
      result.textContent = '';
      term.focus();
      term.select();
    },

    close(): void {
      options.onClose();
      element.setAttribute('hidden', 'hidden');
    },

    isOpen(): boolean {
      return !element.hasAttribute('hidden');
    },

    term: () => term.value,
    replacement: () => replacement.value,

    setResult(text: string): void {
      result.textContent = text;
    },
  };

  return bar;
}
